"use client";
import {m, useMotionValue, useTransform, useSpring} from "framer-motion";
import Image from "next/image";
import {useState, useEffect} from "react";
import {Skeleton} from "@/components/ui/Skeleton";
import {ProjectProps} from "@/types/types";
import {Trash2, Plus, EyeOff} from "lucide-react";

export default function ProjectCard({
  slug,
  title,
  cover,
  url,
  description,
  category,
  tags,
  tech,
  id,
  private: isPrivate,
}: ProjectProps) {
  const [loaded, setLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [shouldTilt, setShouldTilt] = useState(false);

  useEffect(() => {
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setShouldTilt(!isTouch && !prefersReduced);
  }, []);

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Map mouse coordinates to degrees of rotation (-10 to 10 degrees)
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [10, -10]), { stiffness: 200, damping: 25 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-10, 10]), { stiffness: 200, damping: 25 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left - width / 2;
    const mouseY = e.clientY - rect.top - height / 2;
    x.set(mouseX / width);
    y.set(mouseY / height);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    x.set(0);
    y.set(0);
  };

  const handleClick = () => {
    window.open(url, "_blank");
  };

  const notifyChange = () => {
    // Trigger ProjectsSection to re-fetch the merged public+private list
    window.dispatchEvent(new CustomEvent("projects-changed"));
  };

  const handleDemote = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(`Move "${title}" back to local drafts?`)) return;
    try {
      const res = await fetch("/api/projects", {
        method: "PUT",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({id}),
      });
      if (res.ok) {
        notifyChange();
      } else {
        const data = await res.json();
        alert(`Error: ${data.error}`);
      }
    } catch {
      alert("Failed to demote project");
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }
    try {
      const res = await fetch(`/api/projects?id=${id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        notifyChange();
      } else {
        const data = await res.json();
        alert(`Error: ${data.error}`);
      }
    } catch {
      alert("Failed to delete project");
    }
  };

  const handlePromote = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(`Are you sure you want to promote "${title}" to production?`)) {
      return;
    }
    try {
      const res = await fetch("/api/projects", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({id}),
      });
      if (res.ok) {
        notifyChange();
      } else {
        const data = await res.json();
        alert(`Error: ${data.error}`);
      }
    } catch {
      alert("Failed to promote project");
    }
  };

  return (
    <div className="relative w-full h-full" style={{ perspective: 1000 }}>
      <m.div
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        style={{
          rotateX: shouldTilt ? rotateX : 0,
          rotateY: shouldTilt ? rotateY : 0,
          transformStyle: "preserve-3d",
        }}
        className={`group relative flex flex-col h-full bg-zinc-900/50 border-2 transition-none cursor-pointer rounded-none select-none ${
          isHovered ? "border-yellow-400" : "border-zinc-800"
        }`}
      >
        {/* Cover image wrapper */}
        <div className="relative h-48 w-full overflow-hidden border-b-2 border-zinc-800 shrink-0">
          {!loaded && (
            <Skeleton
              type="block"
              shimmer
              className="absolute inset-0 h-full w-full rounded-none"
            />
          )}
          <Image
            src={cover}
            alt={title}
            width={800}
            height={500}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            loading="lazy"
            onLoad={() => {
              setLoaded(true);
            }}
            className={`w-full h-full object-cover grayscale brightness-90 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-300 ${
              loaded ? "opacity-100" : "opacity-0"
            }`}
          />
          {/* Flat overlays */}
          <div 
            aria-hidden="true"
            className="absolute inset-0 bg-yellow-400/5 mix-blend-overlay group-hover:bg-transparent transition-all duration-300" 
          />
        </div>

        {/* Flat info container */}
        <div className="flex-1 flex flex-col justify-between p-5 font-mono">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-yellow-400">
                {category}
              </span>
              {isPrivate && (
                <span className="px-2 py-0.5 text-[10px] font-bold bg-zinc-800 text-zinc-400 border border-zinc-700">
                  DRAFT
                </span>
              )}
            </div>

            <h3 className="text-lg font-black uppercase text-zinc-50 tracking-tighter mb-2 group-hover:text-yellow-400 transition-colors">
              {title}
            </h3>

            <p className="text-xs text-zinc-400 leading-relaxed mb-4 line-clamp-3">
              {description}
            </p>
          </div>

          <div>
            {/* Tech tag list */}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {tags.map((tag, i) => (
                <span
                  key={`${slug}-tag-${i}`}
                  className="px-2 py-0.5 text-[10px] bg-zinc-950 text-zinc-300 border border-zinc-800"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between text-[10px] text-zinc-500 border-t border-zinc-800/80 pt-3">
              <span className="truncate max-w-[150px]">{tech}</span>
              <span className="text-yellow-400 font-bold group-hover:translate-x-1 transition-transform duration-150">
                VISIT_SITE &gt;&gt;
              </span>
            </div>
          </div>
        </div>

        {/* Development Mode Management Buttons */}
        {process.env.NODE_ENV === "development" && (
          <div className="absolute top-3 right-3 z-30 flex gap-1.5">
            {isPrivate ? (
              // Draft: promote to production
              <button
                onClick={handlePromote}
                className="flex items-center justify-center p-1.5 bg-emerald-500 text-zinc-950 border border-zinc-950 font-bold hover:bg-emerald-400 transition-none cursor-pointer"
                title="Add to Production"
              >
                <Plus className="w-4 h-4" />
              </button>
            ) : (
              // Published: demote back to draft
              <button
                onClick={handleDemote}
                className="flex items-center justify-center p-1.5 bg-yellow-400 text-zinc-950 border border-zinc-950 font-bold hover:bg-yellow-300 transition-none cursor-pointer"
                title="Move to Drafts"
              >
                <EyeOff className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={handleDelete}
              className="flex items-center justify-center p-1.5 bg-red-600 text-zinc-50 border border-zinc-950 font-bold hover:bg-red-500 transition-none cursor-pointer"
              title="Delete Project"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </m.div>
    </div>
  );
}
